import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Crown } from "lucide-react";
import type { RoomMemberSummary } from "@/lib/api";

interface MemberListProps {
  members: RoomMemberSummary[];
  ownerSteamId: string;
  meSteamId?: string;
}

export function MemberList({ members, ownerSteamId, meSteamId }: MemberListProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Members</span>
          <span className="text-sm font-normal text-muted-foreground">
            {members.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">No one here yet.</p>
        ) : (
          members.map((m) => {
            const isOwner = m.steamId === ownerSteamId;
            const isMe = m.steamId === meSteamId;
            const synced = !!m.lastLibrarySyncAt;
            return (
              <div
                key={m.steamId}
                className="flex items-center gap-3 rounded-md border border-border/40 bg-card/40 p-2"
              >
                <Avatar className="h-9 w-9">
                  {m.avatarUrl && <AvatarImage src={m.avatarUrl} alt={m.displayName} />}
                  <AvatarFallback>{m.displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex flex-1 flex-col min-w-0">
                  <span className="flex items-center gap-1 text-sm font-medium truncate">
                    {m.displayName}
                    {isMe && (
                      <span className="text-xs text-muted-foreground">(you)</span>
                    )}
                    {isOwner && (
                      <Crown className="h-3.5 w-3.5 text-amber-400" aria-label="Host" />
                    )}
                  </span>
                  <Badge
                    variant={synced ? "secondary" : "muted"}
                    className="self-start text-[10px]"
                  >
                    {synced ? "Library synced" : "Awaiting sync"}
                  </Badge>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
